import { useState, useRef, useEffect } from 'react'
import { validateEmail } from '@/shared/utils';
import { Button, Spacing, Textbox, RadioGroup, RadioOption } from '@/form/FormComponents'
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider'
import { SignInContainer } from '@/pages/SignedOutLayout'
import { useErrorModal, useModal, Modal, ModalContent} from '@/components/Modal';
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { verifyEmailOobCode, verifyPasswordResetOobCode, confirmPasswordReset } from '@/systems/Authentication'
import { useParams, useNavigate, useSearchParams } from 'react-router'

export function VerifyEmailLayout() {
  const navigate = useNavigate();
  const oobCode = useParams().oobCode;
  const [searchParams] = useSearchParams();
  const change = searchParams.has("change");
  const [errored, setErrored] = useState(false);
  const [success, setSuccess] = useState(false);
  const hasSendEmail = useRef(false);

  useEffect(function() {
    if (hasSendEmail.current) return;
    hasSendEmail.current = true;
    verifyEmailOobCode(oobCode).then(function() {
      if (change) {
        setSuccess(true);
      } else {
        window.location.href = "/login";
      }
    }).catch(function(error) {
      console.error(error);
      setErrored(true);
    });
  }, [oobCode, navigate, change]);

  if (success) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Email changed</div>
        <div className="setup_description">Your email has been changed. You can now sign in with your new email address.</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Continue" className="primary" onClick={() => window.location.href = "/login"}/>
        </div>
      </SignInContainer>
    )
  }

  if (errored) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Couldn't verify your email</div>
        <div className="setup_description">This link is either expired or invalid. Please try verifying your email again.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => navigate("/login", { replace: true })}/>
        </div>
      </SignInContainer>
    )
  }

  return (
    <SignInContainer loading={false}>
      <div className="setup_title">Verifying your email...</div>
      <Spinner style={{width: "50px", height: "50px", margin: "0 auto"}}/>
      <Spacing/>
      <Spacing/>
    </SignInContainer>
  )
}

export function ResetPasswordLayout() {
  const navigate = useNavigate();
  const oobCode = useParams().oobCode;
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [errored, setErrored] = useState(false);

  const hasVerifiedOobCode = useRef(false);

  useEffect(function() {
    if (hasVerifiedOobCode.current) return;
    hasVerifiedOobCode.current = true;
    verifyPasswordResetOobCode(oobCode).then(function() {
      setVerifying(false);
    }).catch(function(error) {
      console.error(error);
      setVerifying(false);
      setErrored(true);
    });
  }, [oobCode]);

  const handleSubmit = function({ formatted_data, focusError, updateData }) {
    if (loading) { return; }

    let password = formatted_data.password;
    let password_verify = formatted_data.password_verify;

    if (password !== password_verify) {
      focusError({name: "password_verify", select: true, error: "Passwords don't match"})
      return;
    }

    setLoading(true);

    confirmPasswordReset(oobCode, password).then(function() {
      setSuccess(true);
    }).catch(function(error) {
      setLoading(false);

      if (error.code == "auth/weak-password") {
        focusError({name: "password", error: "Please choose a stronger password"});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      } else if (error.code == "auth/invalid-action-code") {
        focusError({name: "password", error: "This link is either expired or invalid. Please try resetting your password again."});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      } else {
        console.error(error);
        focusError({name: "password", error: error.message});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      }

    });
  }

  if (success) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Password updated</div>
        <div className="setup_description">You can now sign in with your new password.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => navigate("/login", { replace: true })}/>
        </div>
      </SignInContainer>
    )
  }

  if (errored) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Link expired</div>
        <div className="setup_description">This link is either expired or invalid. Please try resetting your password again.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => navigate("/login", { replace: true })}/>
        </div>
      </SignInContainer>
    )
  }

  if (verifying) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Choose a new password</div>
        <Spinner style={{width: "50px", height: "50px", margin: "0 auto"}}/>
        <Spacing/>
        <Spacing/>
      </SignInContainer>
    )
  }

  return (
    <SignInContainer loading={loading}>
      <FormProvider key="reset_password" onSubmit={handleSubmit} obviated={loading}>
        <FormSubmitProvider>
          {({handleSubmit}) => (
            <form onSubmit={(event) => event.preventDefault()} className="pmf-reset-password">
              <div className="setup_title">Choose a new password</div>
              <Textbox name="password" title="New password" required="Please choose a new password" placeholder="Password" onEnter={() => {}} autofocus={true} type="password" autoComplete="new-password"/>
              <Spacing/>
              <Textbox name="password_verify" title="Re-type new password" required="Please re-type your new password" placeholder="Password" onEnter={handleSubmit} type="password" autoComplete="new-password"/>
              <Spacing/>
              <Spacing/>
              <div className="buttons right">
                <Button text="Change password" className="primary" submit onClick={handleSubmit}/>
              </div>
            </form>
          )}
        </FormSubmitProvider>
      </FormProvider>
    </SignInContainer>
  )
}